import {gql, useQuery} from '@apollo/client'
import {useSelector} from 'react-redux'
import {useStatesAndHealthConditions} from '../../../../../store/hooks/breadCrumbs'
import {selectFilterDetailsLoading} from '../../../../../store/slices/breadCrumbsSlice'
import {getState} from '../../../../../shared/helpers/getState'
import {useFilterManagement} from '../../../../../store/hooks/useFilterManagement'
import {shouldSkipQuery} from '../helpers'

// Base fields always required
const BASE_FIELDS = `
  state: ste
  county: cnty
  censusTractGeoId: ctId
  population: pop
  count: cnt
  censusTractPopulation: ctpop
  countyPercentage: cntyPer
  censusTractPercentage: ctPer
`

// Fields only needed for detailed/outreach view
const OUTREACH_FIELDS = `
  snapEnrolled
  usdaSnapIndCount
  enrolled
  notEnrolled
  usdaSnapIndNotEnrolled
  censusTractSnapEnrolledPercentage: ctSEPer
  censusTractSnapNotEnrolledPercentage: ctSNEPer
`

// Pre-build query variants OUTSIDE the hook at module level
// Variant 1: Base query (no outreach)
const CENSUS_TRACT_QUERY_BASE = gql`
  query censusTract($filters: Filters, $benefits: [BenefitEligibility]) {
    censusTract(filters: $filters, benefits: $benefits) {
      censusTract{
        ${BASE_FIELDS}
      }
      missingGeoIds
    }
  }
`

// Variant 2: Base + outreach fields
const CENSUS_TRACT_QUERY_OUTREACH = gql`
  query censusTract($filters: Filters, $benefits: [BenefitEligibility]) {
    censusTract(filters: $filters, benefits: $benefits) {
      censusTract{
        ${BASE_FIELDS}
        ${OUTREACH_FIELDS}
      }
      missingGeoIds
    }
  }
`
// Helper to pick correct query variant
const getCensusTractQuery = (isOutreach) => {
  return isOutreach ? CENSUS_TRACT_QUERY_OUTREACH : CENSUS_TRACT_QUERY_BASE
}

export default function useCensusTract() {
  const {getQueryParams, getOutreachParams} = useFilterManagement()
  const {filters} = useStatesAndHealthConditions()
  const filterDetailsLoading = useSelector(selectFilterDetailsLoading)

  const appliedFilterParams = getQueryParams()
  const currentStateFromContext = filters && getState(filters)

  // Get outreach params (benefits only for now)
  const outreach = getOutreachParams()
  const benefitsData = outreach.type === 'benefits' ? outreach.data : []
  const censusTractQuery = getCensusTractQuery(benefitsData.length > 0)

  const {
    data: censusTractData,
    loading: loadingCensusTract,
    refetch: refetchCensusTract,
    error: errorLoadingCensusTract
  } = useQuery(censusTractQuery, {
    variables: {
      filters: {
        ...appliedFilterParams,
        state: currentStateFromContext
      },
      benefits: benefitsData
    },
    skip: shouldSkipQuery(filterDetailsLoading, currentStateFromContext),
    partialRefetch: true,
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all'
  })

  return {
    censusTractData,
    loadingCensusTract,
    refetchCensusTract,
    errorLoadingCensusTract
  }
}
